import { useAuth } from '../auth/useAuth';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { apiBaseUrl } from '../api/baseUrl';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

/**
 * OAuth error codes that no amount of retrying will fix, because each one is Auth0 asking
 * the person at the keyboard something. Only a window they can answer gets past these.
 */
const INTERACTIVE_ERRORS = [
    'consent_required',
    'login_required',
    'interaction_required',
    'mfa_required',
];

/** What went wrong while fetching a token, kept whole so a screen can show it as is. */
export interface TokenFailure {
    /** The OAuth error code, e.g. `consent_required`; empty for non-OAuth failures. */
    code: string;
    /** What Auth0 or the browser said, unedited. */
    message: string;
    /** Whether an interactive round stands a chance of resolving it. */
    needsInteraction: boolean;
}

/** The token side of the provider: what failed, and what the user can do about it. */
export interface ApiAuth {
    /** True until a token attempt has finished, successfully or not. */
    loading: boolean;
    /** The failure that left us without a token; null while there is nothing wrong. */
    failure: TokenFailure | null;
    /** True while an interactive round or a retry is running. */
    busy: boolean;
    /** Ask Auth0 in a popup, where consent and login can actually be answered. */
    grantAccess: () => Promise<void>;
    /** Walk the silent route again, for failures that are not about interaction. */
    retry: () => void;
}

/*
    auth0-react rethrows anything carrying an `error` property as an OAuthError, so the code
    survives the trip. Everything else - a network failure, a blocked popup - arrives with
    only a message, and then that message is the whole story.
*/
const describeFailure = (error: unknown): TokenFailure => {
    const raw = (error as { error?: unknown } | null)?.error;
    const code = typeof raw === 'string' ? raw : '';

    return {
        code,
        message: error instanceof Error ? error.message : String(error),
        needsInteraction: INTERACTIVE_ERRORS.includes(code),
    };
};

type AxiosContext = { axiosInstance?: AxiosInstance };
const initialContext: AxiosContext = { axiosInstance: undefined };
const AxiosReactContext = createContext<AxiosContext>(initialContext);

/*
    Deliberately a second context instead of two more fields on the first one.

    Half the screen keys its effects on the axios instance, the SignalR hubs included: they
    tear down and reconnect whenever it changes identity. Hanging the token state off that
    same object would hand them a new instance every time a button turns busy, and
    reconnect a live hub for a spinner.
*/
const ApiAuthContext = createContext<ApiAuth>({
    loading: true,
    failure: null,
    busy: false,
    grantAccess: async () => undefined,
    retry: () => undefined,
});

// 1 component to define an Axios Instance in a Context
export const AxiosProvider: React.FunctionComponent<{ children: ReactNode }> = (
    props
) => {
    const {
        isLoading,
        isAuthenticated,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
    } = useAuth();

    const [token, setToken] = useState<string | null>(null);
    const [failure, setFailure] = useState<TokenFailure | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [busy, setBusy] = useState<boolean>(false);
    const [attempt, setAttempt] = useState<number>(0);

    /*
        The silent route, and the two things it has to get right.

        First the waiting. Child effects run before parent effects, so this one used to fire
        before Auth0Provider had finished its own startup - before the authorization code
        from the login redirect was traded for a token. Asking then means asking an empty
        cache, which sends auth0-spa-js off to a hidden iframe with `prompt=none`. That
        iframe can show nothing, so Auth0 answers `consent_required` for an account that has
        not approved this API yet. Waiting for `isAuthenticated` turns the same call into a
        cache hit.

        Then the failing. A rejection here used to escape as an unhandled promise: a red
        overlay over a dashboard that carried on regardless, and behind that a token of
        `null` in every Authorization header. It is caught now, but nothing is hidden - the
        console keeps the stack, the screen gets the message.
    */
    useEffect(() => {
        if (isLoading || !isAuthenticated) {
            return;
        }

        let cancelled = false;

        setLoading(true);

        (async () => {
            try {
                const next = await getAccessTokenSilently();

                if (!cancelled) {
                    setToken(next);
                    setFailure(null);
                }
            } catch (ex) {
                console.error('Silent token request failed', ex);

                if (!cancelled) {
                    setFailure(describeFailure(ex));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isLoading, isAuthenticated, getAccessTokenSilently, attempt]);

    const retry = useCallback(() => setAttempt((round) => round + 1), []);

    /*
        The interactive route, behind a button.

        A popup is not bound by `prompt=none`, so Auth0 can show the consent screen or the
        login form in it. A full redirect would do the same job, but it throws away
        everything on the dashboard behind it - and this app passes no `onRedirectCallback`,
        so it would drop you on the start page instead of the screen you were reading.
    */
    const grantAccess = useCallback(async () => {
        setBusy(true);

        try {
            const next = await getAccessTokenWithPopup();

            setToken(next ?? null);
            setFailure(null);
        } catch (ex) {
            console.error('Interactive token request failed', ex);
            setFailure(describeFailure(ex));
        } finally {
            setBusy(false);
        }
    }, [getAccessTokenWithPopup]);

    const contextValue: AxiosContext = useMemo(() => {
        let axiosInstance = axios.create({
            baseURL: apiBaseUrl,
            transformResponse: [],
        });

        /*
            No token, no header. `Bearer null` is not an absent header but a broken one: it
            turns "we do not have a token yet" into "this token is invalid", and the API
            logs a rejected caller where it should see an anonymous one.
        */
        if (token) {
            axiosInstance.defaults.headers.common[
                'Authorization'
            ] = `Bearer ${token}`;
        }

        axiosInstance.defaults.headers.common['Access-Control-Allow-Origin'] =
            '*';
        axiosInstance.defaults.headers.common['Access-Control-Allow-Methods'] =
            'GET, POST, PATCH, PUT, DELETE, OPTIONS';
        axiosInstance.defaults.headers.common['Access-Control-Allow-Headers'] =
            'Origin, Content-Type, X-Auth-Token';

        axiosInstance.interceptors.response.use(
            (response: AxiosResponse<any>) => {
                return response;
            },
            (error) => {
                // const problemDetails: any = error.response.data;
                // console.log('problemDetails:', problemDetails);

                // switch (problemDetails.code) {
                //     case ApiErrorCode.EntityNotFound:
                //         toast.error('EntityNotFound!');
                //         break;
                //     case ApiErrorCode.InvalidStatusChange:
                //         toast.error(
                //             `Invalid Status Change! Allowed status: ${(
                //                 problemDetails.additionalDetails
                //                     .allowedStatus as string[]
                //             ).join(', ')}`
                //         );
                //         break;
                //     default:
                //     // Do nothing and let specific custom business exception handling.
                // }

                return Promise.reject(error);
            }
        );

        return { axiosInstance };
    }, [token]);

    const authValue: ApiAuth = useMemo(
        () => ({ loading, failure, busy, grantAccess, retry }),
        [loading, failure, busy, grantAccess, retry]
    );

    return (
        <AxiosReactContext.Provider value={contextValue}>
            <ApiAuthContext.Provider value={authValue}>
                {props.children}
            </ApiAuthContext.Provider>
        </AxiosReactContext.Provider>
    );
};

// 1 custom hook to access the Axios Instance from the Context

export const useAxiosSwagger = () =>
    useContext(AxiosReactContext).axiosInstance;

/** Companion to useAxiosSwagger: the state of the token behind those requests. */
export const useApiAuth = () => useContext(ApiAuthContext);
